import React, { Component } from 'react';
import axios from 'axios';
import ApexLogo from '../apex-logo.png';
import './Sidebar.css';

export class Sidebar extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isAdmin: false,
            isCollapsed: false,
            isMobile: false,
            openMenus: {
                nutriform: true,
                parts: false,
                suppliers: false,
                admin: false,
                library: false
            }
        };
        this.isUpdating = false;
    }

    componentDidMount() {
        this._isMounted = true;
        this.checkRole();
        this.loadSidebarState();
        this.handleResize();
        window.addEventListener('resize', this.debouncedHandleResize);
        window.addEventListener('storage', this.handleStorageChange);
    }

    componentWillUnmount() {
        this._isMounted = false;
        window.removeEventListener('resize', this.debouncedHandleResize);
        window.removeEventListener('storage', this.handleStorageChange);
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
    }


    debouncedHandleResize = () => {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }
        this.resizeTimeout = setTimeout(() => {
            this.handleResize();
        }, 150);
    }

    handleResize = () => {
        if (this.isUpdating || !this._isMounted) return;

        const isMobile = window.innerWidth <= 768;

        // Skip if the mobile state hasn't changed
        if (this.state.isMobile === isMobile) return;

        this.isUpdating = true;

        requestAnimationFrame(() => {
            if (!this._isMounted) {
                this.isUpdating = false;
                return;
            }

            this.setState(prevState => {
                const newState = { isMobile };
                if (isMobile && !prevState.isCollapsed) {
                    newState.isCollapsed = true;
                }
                return newState;
            }, () => {
                this.isUpdating = false;
            });
        });
    }

    loadSidebarState = () => {
        if (!this._isMounted) return;
        const savedState = localStorage.getItem('sidebarCollapsed');
        if (savedState !== null) {
            this.setState({ isCollapsed: JSON.parse(savedState) });
        }
        this.setActiveMenus();
    }

    setActiveMenus = () => {
        if (!this._isMounted) return;
        const currentPath = window.location.pathname;
        const openMenus = { ...this.state.openMenus };

        // Check for NutriFORM submenu pages
        if (currentPath.includes('/overview') || currentPath.includes('/request-quote') || currentPath.includes('/formula') || currentPath.includes('/draft-estimate') || currentPath.includes('/estimate') || currentPath.includes('/new-records')) {
            openMenus.nutriform = true;
        }

        // Check for Parts submenu pages
        if (currentPath.includes('/parts') || currentPath === '/inventory') {
            openMenus.parts = true;
        }

        // Check for Suppliers submenu pages
        if (currentPath.includes('/suppliers') || currentPath.includes('/purchases') || currentPath.includes('/compare-quotes')) {
            openMenus.suppliers = true;
        }

        // Check for Admin submenu pages
        if (currentPath.includes('/users') || currentPath.includes('/admin-suppliers') || currentPath.includes('/roles')) {
            openMenus.admin = true;
        }

        // Check for Library submenu pages
        if (currentPath.includes('/purchasing-library') || currentPath.includes('/quoting-history') || currentPath.includes('/vendors')) {
            openMenus.library = true;
        }

        this.setState({ openMenus });
    }

    checkSidebarState = () => {
        if (!this._isMounted) return;
        const collapsed = JSON.parse(localStorage.getItem('sidebarCollapsed') || 'false');
        if (collapsed !== this.state.isCollapsed) {
            this.setState({ isCollapsed: collapsed });
        }
    }

    handleStorageChange = (e) => {
        if (e.key === 'sidebarCollapsed' && !this.isUpdating && this._isMounted) {
            this.isUpdating = true;
            const collapsed = JSON.parse(e.newValue);
            this.setState({ isCollapsed: collapsed }, () => {
                this.isUpdating = false;
            });
        }
    }

    async checkRole() {
        try {
            const response = await axios.get('/manage/check-role');
            if (this._isMounted && response.data === "Admin") {
                this.setState({ isAdmin: true });
            }
        } catch (error) {
            console.error('Error checking role:', error);
        }
    }

    toggleSidebar = () => {
        if (this.isUpdating) return;
        this.isUpdating = true;

        const newCollapsed = !this.state.isCollapsed;

        // Use requestAnimationFrame to ensure clean DOM updates
        requestAnimationFrame(() => {
            this.setState({ isCollapsed: newCollapsed }, () => {
                requestAnimationFrame(() => {
                    this.isUpdating = false;
                });
            });
            localStorage.setItem('sidebarCollapsed', JSON.stringify(newCollapsed));
        });
    }

    toggleMenu = (menu) => {
        this.setState(prevState => ({
            openMenus: {
                ...prevState.openMenus,
                [menu]: !prevState.openMenus[menu]
            }
        }));
    }

    logout = (e) => {
        e.preventDefault();
        localStorage.removeItem('token');
        localStorage.removeItem('suppliersSearch');
        localStorage.removeItem('suppliersSelectedOption');
        localStorage.removeItem('suppliersSelectedOptionName');
        localStorage.removeItem('suppliersPageSize');
        localStorage.removeItem('rawsPerPage');
        localStorage.removeItem('purchasesPerPage');
        window.location.href = "/login";
    }

    render() {
        const { isCollapsed, openMenus, isAdmin, isMobile } = this.state;
        const currentPath = window.location.pathname;
        const showOverlay = !isCollapsed && isMobile;

        return (
            <div className="sidebar-wrapper">
                <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-logo">
                            <img src={ApexLogo} alt="APEX" className="logo-img" />
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        <ul className="nav-list">
                            <li className={`nav-item ${currentPath === '/home' ? 'active' : ''}`}>
                                <a href="/home" className="nav-link">
                                    <i className="fa-solid fa-house"></i>
                                    {!isCollapsed && <span>Home</span>}
                                </a>
                            </li>

                            <li className="nav-item has-submenu">
                                <div
                                    className={`nav-link ${currentPath.includes('/overview') || currentPath.includes('/request-quote') || currentPath.includes('/formula') || currentPath.includes('/draft-estimate') || currentPath.includes('/estimate') ? 'active' : ''}`}
                                    onClick={() => this.toggleMenu('nutriform')}
                                >
                                    <i className="fa-solid fa-flask"></i>
                                    {!isCollapsed && (
                                        <>
                                            <span>NutriFORM</span>
                                            <i className={`fa-solid fa-chevron-${openMenus.nutriform ? 'right' : 'down'}`} style={{'position': 'absolute', 'right':'13px'}}></i>
                                        </>
                                    )}
                                </div>
                                {openMenus.nutriform && !isCollapsed && (
                                    <ul className="submenu">
                                        <li><a href="/overview" className={currentPath === '/overview' ? 'active' : ''}>Overview</a></li>
                                        <li><a href="/request-quote" className={currentPath === '/request-quote' ? 'active' : ''}>Request For Quote</a></li>
                                        <li><a href="/formula" className={currentPath === '/formula' ? 'active' : ''}>Formula</a></li>
                                        <li><a href="/draft-estimate" className={currentPath === '/draft-estimate' ? 'active' : ''}>Estimate</a></li>
                                        <li><a href="/estimate" className={currentPath === '/estimate' ? 'active' : ''}>Final Quote</a></li>
                                        {/* New Records hidden per client request 2026-05-15 — route + code intentionally retained for future use. */}
                                    </ul>
                                )}
                            </li>

                            <li className="nav-item has-submenu">
                                <div
                                    className={`nav-link ${currentPath.includes('/parts') || currentPath === '/inventory' ? 'active' : ''}`}
                                    onClick={() => this.toggleMenu('parts')}
                                >
                                    <i className="fa-solid fa-box"></i>
                                    {!isCollapsed && (
                                        <>
                                            <span>Parts</span>
                                            <i className={`fa-solid fa-chevron-${openMenus.parts ? 'right' : 'down'}`} style={{'position': 'absolute', 'right':'13px'}}></i>
                                        </>
                                    )}
                                </div>
                                {openMenus.parts && !isCollapsed && (
                                    <ul className="submenu">
                                        <li><a href="/parts" className={currentPath === '/parts' ? 'active' : ''}>Database</a></li>
                                        <li><a href="/inventory" className={currentPath === '/inventory' ? 'active' : ''}>Inventory</a></li>
                                    </ul>
                                )}
                            </li>

                            <li className="nav-item has-submenu">
                                <div
                                    className={`nav-link ${currentPath.includes('/suppliers') || currentPath.includes('/purchases') ? 'active' : ''}`}
                                    onClick={() => this.toggleMenu('suppliers')}
                                >
                                    <i className="fa-solid fa-truck"></i>
                                    {!isCollapsed && (
                                        <>
                                            <span>Suppliers</span>
                                            <i className={`fa-solid fa-chevron-${openMenus.suppliers ? 'right' : 'down'}`} style={{'position': 'absolute', 'right':'13px'}}></i>
                                        </>
                                    )}
                                </div>
                                {openMenus.suppliers && !isCollapsed && (
                                    <ul className="submenu">
                                        <li><a href="/suppliers-data" className={currentPath === '/suppliers-data' ? 'active' : ''}>Supplier List</a></li>
                                        {/* Purchases hidden per client request 2026-05-15 — route + code intentionally retained for future use. */}
                                    </ul>
                                )}
                            </li>

                            {/* Customer Portal hidden per client request 2026-05-15 — markup intentionally retained for future use.
                            <li className={`nav-item ${currentPath === '/customer-portal' ? 'active' : ''}`}>
                                <a href="" className="nav-link">
                                    <i className="fa-solid fa-user"></i>
                                    {!isCollapsed && <span>Customer Portal</span>}
                                </a>
                            </li>
                            */}

                            {isAdmin && (
                                <li className="nav-item has-submenu">
                                    <div
                                        className={`nav-link ${currentPath.includes('/users') || currentPath.includes('/admin-suppliers') || currentPath.includes('/roles') || currentPath.includes('/dropdown-manager') ? 'active' : ''}`}
                                        onClick={() => this.toggleMenu('admin')}
                                    >
                                        <i className="fa-solid fa-crown"></i>
                                        {!isCollapsed && (
                                            <>
                                                <span>Admin</span>
                                                <i className={`fa-solid fa-chevron-${openMenus.admin ? 'right' : 'down'}`} style={{'position': 'absolute', 'right':'13px'}}></i>
                                            </>
                                        )}
                                    </div>
                                    {openMenus.admin && !isCollapsed && (
                                        <ul className="submenu">
                                            <li><a href="/users" className={currentPath === '/users' ? 'active' : ''}>Users</a></li>
                                            <li><a href="/admin-suppliers" className={currentPath === '/admin-suppliers' ? 'active' : ''}>Suppliers</a></li>
                                            <li><a href="/roles" className={currentPath === '/roles' ? 'active' : ''}>Roles</a></li>
                                            <li><a href="/dropdown-manager" className={currentPath === '/dropdown-manager' ? 'active' : ''}>Dropdowns</a></li>
                                        </ul>
                                    )}
                                </li>
                            )}

                            <li className="nav-item has-submenu">
                                <div
                                    className={`nav-link ${currentPath.includes('/purchasing-library') || currentPath.includes('/quoting-history') || currentPath.includes('/vendors') || currentPath === '/library' ? 'active' : ''}`}
                                    onClick={() => this.toggleMenu('library')}
                                >
                                    <i className="fa-solid fa-book"></i>
                                    {!isCollapsed && (
                                        <>
                                            <span>Library</span>
                                            <i className={`fa-solid fa-chevron-${openMenus.library ? 'right' : 'down'}`} style={{'position': 'absolute', 'right':'13px'}}></i>
                                        </>
                                    )}
                                </div>
                                {openMenus.library && !isCollapsed && (
                                    <ul className="submenu">
                                        <li><a href="/purchasing-library" className={currentPath === '/purchasing-library' ? 'active' : ''}>Purchasing Library</a></li>
                                        <li><a href="/quoting-history" className={currentPath === '/quoting-history' ? 'active' : ''}>Quoting History</a></li>
                                        <li><a href="/vendors" className={currentPath === '/vendors' ? 'active' : ''}>Vendors</a></li>
                                    </ul>
                                )}
                            </li>
                        </ul>
                    </nav>

                    <div className="sidebar-footer">
                        <button onClick={this.logout} className="logout-btn">
                            <i className="ti-power-off"></i>
                            {!isCollapsed && <span>Logout</span>}
                        </button>
                    </div>
                </div>

                <button className="mobile-menu-toggle" onClick={this.toggleSidebar}>
                    <i className="ti-menu"></i>
                </button>

                <div
                    className="sidebar-overlay"
                    onClick={this.toggleSidebar}
                    style={{ display: showOverlay ? 'block' : 'none' }}
                ></div>
            </div>
        );
    }
}

export default Sidebar;